import { useKeycloak } from '@/hooks/use-keycloak-wrapper'
import { useEffect, useMemo } from 'react'
import { useTrialAuth } from './use-trial-auth'

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  avatar_url?: string
  roles: string[]
  full_name?: string
  primary_role?: string
  preferred_language?: string
  trial_mode?: boolean
  trial_expires_at?: string
  is_freemium_user?: boolean
  subscription_tier?: string
  school_id?: string | null
  school_type?: string | null
  created_at?: string
  profile?: {
    age?: number
    firstName?: string
    lastName?: string
  }
}

export function useAuth() {
  const { keycloak, initialized } = useKeycloak()
  const { trialUser, isTrialMode, isTrialActive, clearTrialUser, hasHydrated } = useTrialAuth()

  const loading = !initialized || !hasHydrated

  // Extract stable primitives from keycloak object for useMemo deps
  const kcAuthenticated = initialized && keycloak.authenticated
  const kcSub = keycloak.tokenParsed?.sub
  const kcUsername = keycloak.tokenParsed?.preferred_username
  const kcEmail = keycloak.tokenParsed?.email
  const kcAvatar = keycloak.tokenParsed?.avatar_url
  const kcRolesRaw = keycloak.tokenParsed?.realm_access?.roles
  const kcRolesKey = kcRolesRaw ? kcRolesRaw.join(',') : ''

  // Compute user synchronously so it's available on the FIRST render.
  // Using useEffect+useState caused a one-frame null gap that made
  // RoleGuard redirect before the effect could run.
  const user: User | null = useMemo(() => {
    if (loading) return null

    // Priority 1: Active trial mode
    if (isTrialMode && trialUser && isTrialActive()) {
      return {
        id: trialUser.id,
        name: trialUser.full_name,
        email: trialUser.email,
        full_name: trialUser.full_name,
        primary_role: trialUser.primary_role,
        preferred_language: trialUser.preferred_language,
        roles: [trialUser.primary_role],
        trial_mode: true,
        trial_expires_at: trialUser.trial_expires_at,
        school_id: trialUser.school_id || null,
        school_type: trialUser.school_type || null,
      }
    }

    // Priority 2: Keycloak authentication
    if (kcAuthenticated && keycloak.tokenParsed) {
      return {
        id: kcSub || '',
        name: kcUsername || '',
        email: kcEmail || '',
        avatar: kcAvatar,
        roles: kcRolesRaw || [],
        trial_mode: false,
      }
    }

    return null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isTrialMode, trialUser, kcAuthenticated, kcSub, kcUsername, kcEmail, kcAvatar, kcRolesKey])

  // Handle trial expiration as a side effect (cannot run inside useMemo)
  useEffect(() => {
    if (!loading && isTrialMode && trialUser && !isTrialActive()) {
      clearTrialUser()
    }
  }, [loading, isTrialMode, trialUser, isTrialActive, clearTrialUser])

  return {
    user,
    isAuthenticated: !loading && ((keycloak.authenticated && initialized) || (isTrialMode && isTrialActive())),
    isTrialMode: isTrialMode && isTrialActive(),
    isLoading: loading,
    login: () => keycloak.login(),
    logout: () => {
      if (isTrialMode) {
        clearTrialUser()
      } else {
        keycloak.logout()
      }
    },
    token: (() => {
      const finalToken = (hasHydrated && trialUser && isTrialActive()) ? trialUser.access_token : keycloak.token
      return finalToken
    })(),
  }
}
